/**
 * InlineEditField — click-to-edit lifecycle wrapper for one proposal diff cell.
 *
 * Mirrors the CRM table's cell interaction (`lib/base/table/editable-cell.tsx`)
 * so a proposal field behaves the way the same field behaves in a list:
 *
 *   - Enter closes the editor (the value is already in the payload draft —
 *     the child input writes on every change, so "commit" here means "stop
 *     editing", not "persist"). Persisting stays with the card's Save button.
 *   - Escape restores the value the field had when the editor opened.
 *   - Blurring out of the editor closes it.
 *
 * Multi-line and popover-backed editors need the two escape hatches the table
 * cell also needs: Enter must reach a textarea/markdown/multi-value editor
 * verbatim, and focus moving into a PORTALED popover (select, date picker)
 * must not read as "the user left the field".
 */

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { classifyEditorKind } from '@/lib/crm/inline-edit/editorKinds';
import { isPopoverOpenWithin } from '@/lib/crm/inline-edit/isPopoverOpenWithin';

/** Selector for the first control the editor should focus when it opens. */
const FOCUSABLE_SELECTOR =
  'input, textarea, select, [contenteditable="true"], button';

export interface InlineEditFieldProps {
  /** Field type name of the property being edited, when known. */
  fieldTypeName?: string;
  /** Whether the property holds multiple values (its editor owns Enter). */
  isMulti?: boolean;
  /** Close the editor, keeping the current draft value. */
  onCommit: () => void;
  /** Close the editor and restore the value it had when it opened. */
  onCancel: () => void;
  children: ReactNode;
}

export function InlineEditField({
  fieldTypeName,
  isMulti,
  onCommit,
  onCancel,
  children,
}: InlineEditFieldProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const blurRafRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  // Latest-ref for the deferred blur commit: the frame fires after the blur
  // render, so a captured closure could call a stale handler.
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  });

  // Focus the editor's first control on open. Reaching into the subtree is the
  // sanctioned use of a ref (measure/focus): the child is a canonical
  // PropertyInput whose internal control varies by field type, so there is no
  // single ref to thread through.
  useEffect(() => {
    const el =
      wrapperRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    el?.focus();
  }, []);

  useEffect(
    () => () => {
      if (blurRafRef.current !== null) {
        cancelAnimationFrame(blurRafRef.current);
      }
    },
    []
  );

  const handleBlur = useCallback((e: React.FocusEvent) => {
    // Focus moving between controls inside the editor is not a close.
    if (wrapperRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    if (blurRafRef.current !== null) {
      cancelAnimationFrame(blurRafRef.current);
    }
    // Defer a frame: a react-aria popover opening from this editor moves focus
    // out of the subtree before the popover itself receives it.
    blurRafRef.current = requestAnimationFrame(() => {
      blurRafRef.current = null;
      if (cancelledRef.current) {
        cancelledRef.current = false;
        return;
      }
      const el = wrapperRef.current;
      if (!el || el.contains(document.activeElement)) {
        return;
      }
      // An open popover portals outside the wrapper — the user is still in
      // this field. react-aria marks the trigger with aria-expanded.
      if (isPopoverOpenWithin(el)) {
        return;
      }
      onCommitRef.current();
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cancelledRef.current = true;
        onCancel();
        return;
      }
      if (e.key !== 'Enter') {
        return;
      }
      if (
        classifyEditorKind({
          propertyType: fieldTypeName
            ? ({
                field_type_name: fieldTypeName,
                is_multi: isMulti ?? false,
              } as PropertyTypeSchema)
            : undefined,
        }).ownsEnterKey
      ) {
        return;
      }
      // A select/date popover needs Enter to confirm its own selection.
      if (isPopoverOpenWithin(wrapperRef.current)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onCommit();
    },
    [fieldTypeName, isMulti, onCancel, onCommit]
  );

  return (
    <div
      ref={wrapperRef}
      className='min-w-0'
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
}
